import React, { useCallback, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Box } from '@mui/material';
import { aiNoteActions, appointmentActions, formActions, noteActions } from 'store';
import { FormBuilder } from '../../../../../pages/forms/formBuilder';
import { formModelEnums } from '../../../../../pages/forms/constants';
import { findAiSummaryAttribute } from '../../../../../pages/forms/utils/findAiSummaryAttribute';
import { useAiSessionNoteStream } from '../../../../../utils/hooks/useAiSessionNoteStream';
import { sanitizeAiHtml } from '../../../../../utils/sanitizeAiHtml';
import { noteStatusEnums, OrgIntegrations } from 'utils';
import { useNoteEditPermissions } from '../../../../../pages/forms/constants';

export const CreateAppFormNote = ({ info, apptId, afterSuccess, noteType, serviceTemplate }) => {
    const dispatch = useDispatch();
    const formRef = useRef(null);
    const ACTION_TYPE = 'ADD_EDIT_APPT_NOTE';
    const noteTypeEnum = noteType === 'OFFICE_NOTES' ? formModelEnums.OFFICENOTES : formModelEnums.CLINICALNOTES;

    const formById = useSelector((state) => state.forms?.form);
    const aiResult = useSelector((state) => state.aiNote?.sessionNote);
    const apptById = useSelector((state) => state.appointment.appointmentById);
    const { aiBridge } = OrgIntegrations();
    const { checkSuperVizer, needApproved } = useNoteEditPermissions(info, apptById);

    /**
     * AI summary auto-generation is a clinical-notes-only feature. The form
     * designer marks one RICH_TEXT field with `properties.aiSummary`; when
     * that template loads here for a NEW note (no `info?.id`), generation
     * auto-fires and chunks stream into that field via path-based addressing.
     */
    const aiTargetPathRef = useRef(null);
    /** formId captured at AI-trigger time. Used by `onUpdate` to drop
     *  chunks that arrive after the user has switched templates — the
     *  cleanup effect runs *after* React commits the new form, so without
     *  this synchronous guard a chunk landing between commit and effect
     *  would write to the old path inside the new form. */
    const aiTargetFormIdRef = useRef(null);
    const triggeredRef = useRef(false);
    const lastConvIdRef = useRef(null);
    /** Tracks the formId we're currently wired to. When this changes we
     *  must cancel the in-flight AI stream — otherwise late chunks from
     *  the previous form's conversation land in the new form. */
    const lastFormIdRef = useRef(null);
    /** Mirrors `formById` synchronously on every render so `onUpdate` can
     *  read the latest id without depending on effect scheduling. */
    const formByIdRef = useRef(null);
    formByIdRef.current = formById;

    const onUpdate = useCallback(({ content }) => {
        const targetPath = aiTargetPathRef.current;
        const targetFormId = aiTargetFormIdRef.current;
        const currentFormId = formByIdRef.current?.id || null;
        const ref = formRef.current;
        if (!targetPath || !ref) return;
        if (!targetFormId || targetFormId !== currentFormId) return;
        ref.setFieldByPath?.(targetPath, sanitizeAiHtml(content));
    }, []);

    const stream = useAiSessionNoteStream({ onUpdate });
    /** stream is recreated each render; ref keeps the latest stop() callable
     *  without forcing the effects below to re-run on every render. */
    const stopStreamRef = useRef(stream.stop);
    stopStreamRef.current = stream.stop;

    /** Opening an existing note for edit counts as viewing it (creator is filtered server-side). */
    useEffect(() => {
        if (info?.id) dispatch(noteActions.viewNote({ ids: [info.id] }));
    }, [info?.id]);

    /** Reset on mount + cleanup on unmount: avoids stale `state.aiNote` /
     *  `state.forms.form` carrying over between modal opens. */
    useEffect(() => {
        dispatch(aiNoteActions.resetAiSessionNote());
        return () => {
            dispatch(aiNoteActions.resetAiSessionNote());
            dispatch(formActions.resetFormById());
            dispatch(formActions.resetFormDefault());
        };
    }, [dispatch]);

    /** Form-change watcher + auto-trigger.
     *
     *  When the loaded template ID changes (user picked a different template
     *  while the modal is still open, or the modal mounted with a fresh
     *  template), we must:
     *    1. stop reacting to chunks from the previous conversation,
     *    2. clear all path/trigger refs,
     *    3. reset Redux state.aiNote so the inline indicator and stream
     *       wiring start clean,
     *    4. then re-evaluate the new template for an aiSummary attribute.
     *
     *  Without step 1–3, late chunks from the previous form's stream still
     *  land in the new form (at the OLD path → wrong field). */
    useEffect(() => {
        const currentFormId = formById?.id || null;
        const prevFormId = lastFormIdRef.current;

        if (prevFormId && prevFormId !== currentFormId) {
            stopStreamRef.current?.();
            aiTargetPathRef.current = null;
            aiTargetFormIdRef.current = null;
            triggeredRef.current = false;
            lastConvIdRef.current = null;
            dispatch(aiNoteActions.resetAiSessionNote());
        }
        lastFormIdRef.current = currentFormId;

        // Auto-trigger evaluation
        if (!aiBridge) return;
        if (info?.id) return;
        if (!apptId || !currentFormId) return;
        if (triggeredRef.current) return;

        const match = findAiSummaryAttribute(formById);
        if (!match) return;

        aiTargetPathRef.current = match.path;
        aiTargetFormIdRef.current = currentFormId;
        triggeredRef.current = true;
        dispatch(aiNoteActions.generateAiSessionNote(apptId, currentFormId));
    }, [apptId, dispatch, formById, info?.id, aiBridge]);

    /** Start streaming once the saga returns a conversationId. */
    useEffect(() => {
        if (!aiResult?.conversationId) return;
        if (lastConvIdRef.current === aiResult.conversationId) return;
        lastConvIdRef.current = aiResult.conversationId;
        stream.start(aiResult.conversationId);
    }, [aiResult, stream]);

    /** Mirror local stream status into Redux so the inline AI-loading
     *  indicator (next to AI Summary label) can flip off without reaching
     *  back into this component's state. */
    useEffect(() => {
        if (stream.isDone) dispatch(aiNoteActions.markAiNoteStreamDone());
        else if (stream.isError) dispatch(aiNoteActions.markAiNoteStreamError());
    }, [stream.isDone, stream.isError, dispatch]);

    const handleSaveNote = (data) => {
        const noteData = { ...data, resource: apptId, type: noteType, status: noteStatusEnums.COMPLETED };
        delete noteData?.id;
        if (info) noteData.id = info?.id;
        noteData.status = noteStatusEnums.COMPLETED;
        dispatch(appointmentActions.addEditApptNote(apptId, noteData));
    };

    return (
        <Box sx={{ width: 816 }}>
            <FormBuilder
                ref={formRef}
                info={info}
                resourceId={apptId}
                formModel={noteTypeEnum}
                afterSuccess={afterSuccess}
                ACTION_TYPE={ACTION_TYPE}
                handleSaveNote={handleSaveNote}
                serviceTemplate={serviceTemplate}
            />
        </Box>
    );
};
